/**
 * OPG-0130 (variant A, forudbetalt periode): planer med samme «gruppe» (pricing_plans.plan_group,
 * V525) og samme kategori vises som ÉT kort med en periodevælger (fx 1, 3 og 6 måneder).
 * Købet bruger den valgte plan uændret, så her er kun visning: hvilke planer hører sammen,
 * hvilken der er valgt fra start, og hvad periodevalget hedder.
 *
 * ⛔ Kopi af apps/holdstyring/src/lib/pricing/plan-groups.ts (widget-bundlen kan ikke
 * importere fra appen). Ret begge; apps/holdstyring/src/__tests__/lib/pricing/plan-groups.test.ts
 * kører de samme tilfælde mod dem begge.
 */

export const PLAN_GROUP_MAX = 100;

export type PeriodeNoegle = 'fourWeeks' | 'oneMonth' | 'threeMonths' | 'sixMonths' | 'twelveMonths';

export interface GrupperbarPlan {
  id: string;
  planGroup?: string | null;
  membershipCategory?: string | null;
  validityDays?: number | null;
  isPopular?: boolean | null;
}

export interface PlanKort<T extends GrupperbarPlan> {
  /** Stabil nøgle til React/Preact: `plan:<id>` eller `gruppe:<kategori>|<navn>`. */
  noegle: string;
  /** Gruppens navn (kortets overskrift), eller null for en plan med sit eget kort. */
  gruppe: string | null;
  /** Planerne på kortet, sorteret efter periode (korteste først). Én plan uden gruppe. */
  planer: T[];
  /** Planen, der er valgt fra start: den populære, ellers den korteste periode. */
  standard: T;
  /** Kan valgene hedde «1 måned», «3 måneder» …? Ellers bruges planernes egne navne. */
  periodeEtiketter: boolean;
}

/** Gruppenavnet, som API'et gemmer det: trimmet, højst 100 tegn, tomt = null. */
export function normaliserPlanGruppe(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const navn = raw.trim().slice(0, PLAN_GROUP_MAX).trim();
  return navn === '' ? null : navn;
}

const PERIODER: Record<number, { noegle: PeriodeNoegle; maaneder: number | null }> = {
  28: { noegle: 'fourWeeks', maaneder: null },
  30: { noegle: 'oneMonth', maaneder: 1 },
  90: { noegle: 'threeMonths', maaneder: 3 },
  180: { noegle: 'sixMonths', maaneder: 6 },
  365: { noegle: 'twelveMonths', maaneder: 12 },
};

/** Periodevalgets etiket ud fra planlængden (samme længder som appens lib/payments/billing-interval.ts). */
export function periodeNoegle(validityDays: number | null | undefined): PeriodeNoegle | null {
  return validityDays != null ? (PERIODER[validityDays]?.noegle ?? null) : null;
}

/** Antal måneder i perioden (til «svarer til X pr. måned»), eller null. */
export function maanederFor(validityDays: number | null | undefined): number | null {
  return validityDays != null ? (PERIODER[validityDays]?.maaneder ?? null) : null;
}

const gruppeNoegle = (plan: GrupperbarPlan): string | null => {
  const navn = normaliserPlanGruppe(plan.planGroup);
  if (!navn) return null;
  return `${(plan.membershipCategory ?? '').trim().toLowerCase()}|${navn.toLowerCase()}`;
};

/**
 * Plankortene i visningsrækkefølge. En gruppe står på sin første plans plads. En gruppe med
 * kun én (synlig) plan bliver et almindeligt kort, så en halvt oprettet gruppe ser ud som før.
 */
export function grupperPlaner<T extends GrupperbarPlan>(planer: T[]): PlanKort<T>[] {
  const ud: Array<{ noegle: string; gruppe: string | null; planer: T[] }> = [];
  const gruppeIndeks = new Map<string, number>();
  for (const plan of planer) {
    const noegle = gruppeNoegle(plan);
    if (noegle === null) {
      ud.push({ noegle: `plan:${plan.id}`, gruppe: null, planer: [plan] });
      continue;
    }
    const idx = gruppeIndeks.get(noegle);
    if (idx === undefined) {
      gruppeIndeks.set(noegle, ud.length);
      ud.push({ noegle: `gruppe:${noegle}`, gruppe: normaliserPlanGruppe(plan.planGroup), planer: [plan] });
    } else {
      ud[idx].planer.push(plan);
    }
  }
  return ud.map((kort) => {
    if (kort.planer.length < 2) {
      const [plan] = kort.planer;
      return { noegle: `plan:${plan.id}`, gruppe: null, planer: kort.planer, standard: plan, periodeEtiketter: false };
    }
    // Stabil sortering: korteste periode først, planer uden længde sidst.
    const planerSorteret = kort.planer
      .map((plan, i) => ({ plan, i }))
      .sort((a, b) => (a.plan.validityDays ?? Infinity) - (b.plan.validityDays ?? Infinity) || a.i - b.i)
      .map((x) => x.plan);
    const noegler = planerSorteret.map((p) => periodeNoegle(p.validityDays));
    const periodeEtiketter = noegler.every((n) => n !== null) && new Set(noegler).size === noegler.length;
    const standard = planerSorteret.find((p) => p.isPopular) ?? planerSorteret[0];
    return { ...kort, planer: planerSorteret, standard, periodeEtiketter };
  });
}

/** Den valgte plan på kortet; et ukendt eller manglende id giver standardvalget. */
export function valgtPlan<T extends GrupperbarPlan>(kort: PlanKort<T>, valgtId?: string | null): T {
  return kort.planer.find((p) => p.id === valgtId) ?? kort.standard;
}
